import React, {useCallback} from 'react';
import SentryEchart from "../../../../components/echarts/sentry-echart.jsx";
import {scalePx} from "../../../../utils/responsive-graph.js";

const COMPACT_SIZE = 340;
const DETAIL_FONT_WEIGHT = 600;
const MAIN_TITLE_FONT_WEIGHT = 500;
const SUB_TITLE_FONT_WEIGHT = 400;

const DutyDispatchGauge = ({dispatch, dispatchSource, height, className}) => {
    const buildOption = useCallback((size) => {
        const s = Math.min(size.width, size.height);
        const isCompact = s < COMPACT_SIZE;

        const webGenerate = dispatchSource["WEB_GENERATED"] || 0;
        const selfGenerated = dispatchSource["SELF_GENERATED"] || 0;
        const leaderGenerated = dispatchSource["LEADER_GENERATED"] || 0;
        const safeDispatch = dispatch || 1;

        const mainDetailFs = scalePx(s, 34, 409, 12, 56) + 1;
        const mainTitleFs = scalePx(s, 18, 409, 11, 30) + 1;
        const mainTitleOffset = scalePx(s, 95, 409, 30, 140) + 8;

        const subDetailFs = scalePx(s, 24, 409, 12, 40) + 1;
        const subTitleFs = scalePx(s, 14, 409, 11, 22) + 1;
        const subTitleOffset = scalePx(s, 58, 409, 18, 80) + 8;

        const tickMainLen = scalePx(s, 25, 409, 8, 44);
        const tickSubLen = scalePx(s, 13, 409, 5, 22);

        const color = {
            type: 'linear',
            x: 0, y: 0, x2: 1, y2: 0,
            colorStops: [
                { offset: 0, color: '#1CEAE6' },
                { offset: 0.33, color: '#3E98D9' },
                { offset: 0.66, color: '#0080ED' },
                { offset: 1, color: '#003878' }
            ]
        }

        return {
            tooltip: {show: false},
            series: [
                {
                    name: 'Despachos',
                    type: 'gauge',
                    center: ['50%', '25%'],
                    radius: '45%',
                    startAngle: 90,
                    endAngle: -270,
                    min: 0,
                    max: safeDispatch,
                    splitNumber: isCompact ? 6 : 12,
                    silent: true,
                    axisLine: {
                        show: false,
                        lineStyle: {width: 0, color: [[1, color]]}
                    },
                    axisTick: {
                        show: true,
                        splitNumber: 5,
                        length: tickMainLen,
                        lineStyle: {color: 'auto', width: 3}
                    },
                    splitLine: {show: false},
                    axisLabel: {show: false},
                    pointer: {show: false},
                    progress: {show: false},
                    detail: {
                        formatter: '{value}',
                        color: "#ffffff",
                        fontSize: mainDetailFs,
                        fontWeight: DETAIL_FONT_WEIGHT,
                        offsetCenter: !isCompact ? [0, 5] : [0, 3]
                    },
                    title: {
                        show: true,
                        offsetCenter: [0, mainTitleOffset],
                        color: '#aaaaaa',
                        fontSize: mainTitleFs,
                        fontWeight: MAIN_TITLE_FONT_WEIGHT
                    },
                    data: [{value: dispatch, name: 'Despachos'}]
                },
                {
                    name: 'Central',
                    type: 'gauge',
                    center: ['15%', '75%'],
                    radius: '27%',
                    startAngle: 90,
                    endAngle: -270,
                    min: 0,
                    max: safeDispatch,
                    splitNumber: !isCompact ? 8 : 4,
                    silent: true,
                    axisLine: {
                        show: false,
                        lineStyle: {
                            width: 0,
                            color: [[webGenerate / safeDispatch, color], [1, 'rgba(255, 255, 255, 0.08)']]
                        }
                    },
                    axisTick: {
                        show: true,
                        splitNumber: 5,
                        length: tickSubLen,
                        color: [[1, 'rgba(255, 255, 255, 0.08)']],
                        lineStyle: {color: 'auto', width: 2}
                    },
                    splitLine: {show: false},
                    axisLabel: {show: false},
                    pointer: {show: false},
                    detail: {
                        offsetCenter: [0, 3],
                        formatter: '{value}',
                        color: "#ffffff",
                        fontSize: subDetailFs,
                        fontWeight: DETAIL_FONT_WEIGHT
                    },
                    title: {
                        show: true,
                        offsetCenter: [0, subTitleOffset],
                        color: '#aaaaaa',
                        fontSize: subTitleFs,
                        fontWeight: SUB_TITLE_FONT_WEIGHT
                    },
                    data: [{value: webGenerate, name: 'Central'}]
                },
                {
                    name: 'Auto-despacho',
                    type: 'gauge',
                    center: ['50%', '75%'],
                    radius: '27%',
                    startAngle: 90,
                    endAngle: -270,
                    min: 0,
                    max: safeDispatch,
                    splitNumber: !isCompact ? 8 : 4,
                    silent: true,
                    axisLine: {
                        show: false,
                        lineStyle: {
                            width: 0,
                            color: [[selfGenerated / safeDispatch, color], [1, 'rgba(255, 255, 255, 0.08)']]
                        }
                    },
                    axisTick: {
                        show: true,
                        splitNumber: 5,
                        length: tickSubLen,
                        color: [[1, 'rgba(255, 255, 255, 0.08)']],
                        lineStyle: {color: 'auto', width: 2}
                    },
                    splitLine: {show: false},
                    axisLabel: {show: false},
                    pointer: {show: false},
                    detail: {
                        offsetCenter: [0, 3],
                        formatter: '{value}',
                        color: "#ffffff",
                        fontSize: subDetailFs,
                        fontWeight: DETAIL_FONT_WEIGHT
                    },
                    title: {
                        show: true,
                        offsetCenter: [0, subTitleOffset],
                        color: '#aaaaaa',
                        fontSize: subTitleFs,
                        fontWeight: SUB_TITLE_FONT_WEIGHT
                    },
                    data: [{value: selfGenerated, name: 'Auto-despacho'}]
                },
                {
                    name: 'Responsável',
                    type: 'gauge',
                    center: ['85%', '75%'],
                    radius: '27%',
                    startAngle: 90,
                    endAngle: -270,
                    min: 0,
                    max: safeDispatch,
                    splitNumber: !isCompact ? 8 : 4,
                    silent: true,
                    axisLine: {
                        show: false,
                        lineStyle: {
                            width: 0,
                            color: [[leaderGenerated / safeDispatch, color], [1, 'rgba(255, 255, 255, 0.08)']]
                        }
                    },
                    axisTick: {
                        show: true,
                        splitNumber: 5,
                        length: tickSubLen,
                        color: [[1, 'rgba(255, 255, 255, 0.08)']],
                        lineStyle: {color: 'auto', width: 2}
                    },
                    splitLine: {show: false},
                    axisLabel: {show: false},
                    pointer: {show: false},
                    detail: {
                        offsetCenter: [0, 3],
                        formatter: '{value}',
                        color: "#ffffff",
                        fontSize: subDetailFs,
                        fontWeight: DETAIL_FONT_WEIGHT
                    },
                    title: {
                        show: true,
                        offsetCenter: [0, subTitleOffset],
                        color: '#aaaaaa',
                        fontSize: subTitleFs,
                        fontWeight: SUB_TITLE_FONT_WEIGHT
                    },
                    data: [{value: leaderGenerated, name: 'Responsável'}]
                }
            ]
        };
    }, [dispatch, dispatchSource]);

    return (
        <SentryEchart
            className={className}
            option={buildOption}
            height={height}
        />
    );
};

export default DutyDispatchGauge;
